import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Building2, Check, Search } from 'lucide-react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@/common/components/ui/button';
import { Input } from '@/common/components/ui/input';
import { DialogHeader, DialogTitle, DialogDescription } from '@/common/components/ui/dialog';
import { cn } from '@/lib/utils';
import { organizationQueries } from '@/features/organizations/queries';
import { SupplyPointService } from '@/features/supply-points/services/SupplyPointService';
import { supplyPointQueryKeys } from '@/features/supply-points/queries';
import type { ModalRenderProps } from '@/common/components/modal-provider/types';

function SelectedOrgNote({ orgName }: { orgName: string }) {
  return (
    <div className="rounded-xl bg-muted/50 border border-border px-4 py-3 text-sm text-muted-foreground">
      <Trans>
        All members of <strong className="text-foreground">{orgName}</strong> will inherit access to
        this supply point through their organization membership.
      </Trans>
    </div>
  );
}

type Props = Pick<ModalRenderProps<void>, 'resolve'> & {
  supplyPointId: number;
  alreadyAddedPartnerIds: number[];
};

export function AddOrgDialogContent({ supplyPointId, alreadyAddedPartnerIds, resolve }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const { data: orgs } = useSuspenseQuery(organizationQueries.list());

  const available = orgs.filter(
    (o) =>
      !alreadyAddedPartnerIds.includes(o.id) && o.name.toLowerCase().includes(search.toLowerCase())
  );

  const addMutation = useMutation({
    mutationFn: (partnerId: number) => SupplyPointService.addPartners(supplyPointId, [partnerId]),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all });
      resolve();
    },
  });

  const selectedOrg = orgs.find((o) => o.id === selectedId);

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-3 mb-1">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10">
            <Building2 size={20} className="text-primary" />
          </div>
          <div>
            <DialogTitle>
              <Trans>Add organization</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>Grants all members of the organization access to this supply point.</Trans>
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <div className="space-y-4">
        <div className="relative">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            placeholder={t`Search organizations...`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="space-y-1 max-h-56 overflow-y-auto px-1 -mx-1 py-0.5 -my-0.5">
          {available.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              <Trans>No organizations found</Trans>
            </p>
          ) : (
            available.map((org) => (
              <button
                key={org.id}
                type="button"
                onClick={() => setSelectedId(selectedId === org.id ? null : org.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors',
                  selectedId === org.id
                    ? 'bg-primary/5 ring-1 ring-primary/20'
                    : 'hover:bg-muted/50'
                )}
              >
                <div className="size-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-sm font-bold shrink-0">
                  {org.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{org.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {org.city}, {org.country}
                  </div>
                </div>
                {selectedId === org.id && <Check size={16} className="text-primary shrink-0" />}
              </button>
            ))
          )}
        </div>

        {selectedOrg && <SelectedOrgNote orgName={selectedOrg.name} />}

        <div className="flex justify-end gap-3 pt-1">
          <Button variant="outline" type="button" onClick={() => resolve()}>
            <Trans>Cancel</Trans>
          </Button>
          <Button
            onClick={() => selectedId && addMutation.mutate(selectedId)}
            disabled={!selectedId || addMutation.isPending}
          >
            <Trans>Add organization</Trans>
          </Button>
        </div>
      </div>
    </>
  );
}
